
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileItem } from "../files/types";
import { HelpCircle, Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface QAFormProps {
  editingItem: FileItem | null;
  onSaveQA: (question: string, answer: string, category: string) => void;
  onCancelEdit: () => void;
  categories: {value: string, label: string}[];
}

const formSchema = z.object({
  question: z.string().min(3, "Η ερώτηση πρέπει να έχει τουλάχιστον 3 χαρακτήρες"),
  answer: z.string().min(3, "Η απάντηση πρέπει να έχει τουλάχιστον 3 χαρακτήρες"),
  category: z.string()
});

export function QAForm({
  editingItem,
  onSaveQA,
  onCancelEdit,
  categories
}: QAFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: "",
      answer: "",
      category: "qa"
    }
  });

  // Reset form when editing changes
  useEffect(() => {
    if (editingItem) {
      form.reset({
        question: editingItem.question || "",
        answer: editingItem.answer || "",
        category: editingItem.category || "qa"
      });
    } else {
      form.reset({
        question: "",
        answer: "",
        category: "qa"
      });
    }
  }, [editingItem, form]);

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSaveQA(values.question, values.answer, values.category);
    form.reset({
      question: "",
      answer: "",
      category: "qa"
    });
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Κατηγορία</FormLabel>
              <Select 
                value={field.value} 
                onValueChange={field.onChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Επιλέξτε κατηγορία" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ερώτηση</FormLabel>
              <FormControl>
                <Input 
                  {...field}
                  className="mt-1"
                  placeholder="Εισάγετε την ερώτηση εδώ..."
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Απάντηση</FormLabel>
              <FormControl>
                <Textarea 
                  {...field}
                  className="mt-1"
                  placeholder="Εισάγετε την απάντηση εδώ..."
                  rows={5}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end pt-4">
          {editingItem && (
            <Button type="button" variant="outline" onClick={onCancelEdit}>
              <X className="h-4 w-4 mr-2" /> Ακύρωση
            </Button>
          )}
          <Button 
            type="submit"
          >
            {editingItem ? (
              <>
                <Save className="h-4 w-4 mr-2" /> Αποθήκευση αλλαγών
              </>
            ) : (
              <>
                <HelpCircle className="h-4 w-4 mr-2" /> Προσθήκη Ερώτησης & Απάντησης
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
